/**
 * NL-011 (2026-06-09) — Confidence Log card for /check-in.
 *
 * Renders at the top of the client's Daily Check-in once the client has
 * reached Step 3. Pre-fills from `getConfidenceLogEntries(today)` on mount.
 *
 * IMP-013 (2026-06-10) — persistence was hardened after the walkthrough
 * lost an entry (debounce + blur + unmount autosave).
 *
 * CL-EDIT (2026-06-27) — reversed per client request: the Confidence Log
 * no longer autosaves. Saving is deliberate — the explicit Save button is
 * the only write path — and a successful Save now collapses the card for
 * the rest of the day, handing off to the Dashboard's Confidence Log card
 * (which can edit it), exactly like finishing a daily check-in clears it
 * from view. A fresh day brings the card back here.
 *
 * The card silently renders nothing for clients still on Steps 0-2 so
 * the check-in layout doesn't shift mid-coaching. Step gating is
 * driven by the `currentStep` prop the parent passes in (read from
 * `getClientSelf`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';

interface ConfidenceLogCardProps {
  readonly clientId: string;
  readonly currentStep: number;
}

function todayIso(): string {
  // YYYY-MM-DD in the user's local timezone — matches the rest of the
  // Daily Check-in's date semantic (the tracker grid is local-day).
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Whitespace-delimited word count. "more than one word" ⇒ count >= 2. */
function wordCount(s: string): number {
  return s.trim().split(/\s+/).filter(Boolean).length;
}

export function ConfidenceLogCard({
  clientId,
  currentStep,
}: ConfidenceLogCardProps): JSX.Element | null {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const date = todayIso();
  const [text, setText] = useState('');
  const [serverText, setServerText] = useState<string | null>(null);
  // IMP-013 — the "today's entry already written → live on the Dashboard"
  // hand-off is decided ONCE at load time. A save during this session must
  // not yank the card out from under the client mid-thought.
  const [hiddenForToday, setHiddenForToday] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Latest text/baseline for the Save handler, free of stale closure captures.
  const latestRef = useRef<{ text: string; serverText: string | null }>({
    text: '',
    serverText: null,
  });
  latestRef.current = { text, serverText };

  useEffect(() => {
    if (currentStep < 3 || !clientId) return;
    let active = true;
    void (async () => {
      try {
        const entries = await api.getConfidenceLogEntries(accessToken, clientId, {
          from: date,
          to: date,
        });
        if (!active) return;
        const todayEntry = entries.find((e) => e.date === date);
        const initial = todayEntry?.entryText ?? '';
        setText(initial);
        setServerText(initial);
        if (wordCount(initial) >= 2) setHiddenForToday(true);
      } catch {
        if (active) setServerText('');
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, currentStep, date]);

  const persist = useCallback(async (): Promise<void> => {
    const { text: current, serverText: baseline } = latestRef.current;
    if (baseline === null) return; // not hydrated yet
    if (current.trim() === baseline.trim()) return;
    setSaveError(null);
    setSaving(true);
    try {
      const result = await api.upsertConfidenceLogEntry(
        accessToken,
        clientId,
        date,
        current,
      );
      const saved = result?.entryText ?? current;
      setServerText(saved);
      setSavedAt(new Date().toISOString());
      // CL-EDIT — a deliberate Save collapses the card for the rest of the
      // day; today's line now lives (and is editable) on the Dashboard.
      // Empty saves don't collapse (there'd be nothing to hand off).
      if (saved.trim().length > 0) setHiddenForToday(true);
    } catch {
      setSaveError(t('confidenceLog.errors.save'));
    } finally {
      setSaving(false);
    }
  }, [accessToken, clientId, date, t]);

  if (currentStep < 3) return null;
  // Reset-daily: while today's entry loads, render nothing (no flash). Once
  // the client has written a real line for today (more than one word) BEFORE
  // this visit, the Confidence Log lives on the Dashboard for the rest of
  // the day; a fresh day brings it back here.
  if (serverText === null) return null;
  if (hiddenForToday) return null;

  // #13 (2026-06-15) — dirty when the textarea differs from what's persisted
  // server-side. Drives the explicit Save button's enabled + label states.
  // CL-EDIT — the explicit Save button is now the ONLY write path (no
  // autosave); it's independent of the Check-In KPI/activities Save.
  const dirty = serverText !== null && text.trim() !== serverText.trim();

  return (
    <section
      className="mb-s5 rounded-lg border border-bone-3 bg-bone-1 p-s5"
      data-testid="confidence-log-card"
    >
      <header className="mb-s2">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('confidenceLog.eyebrow')}
        </p>
        <h2 className="font-display text-display-sm text-ink mt-s1">
          {t('confidenceLog.title')}
        </h2>
        <p className="mt-s2 text-sm text-gray-2 italic">
          {t('confidenceLog.prompt')}
        </p>
      </header>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('confidenceLog.placeholder')}
        className="input-field min-h-24"
        data-testid="confidence-log-textarea"
        rows={4}
      />
      <div className="mt-s3 flex items-center justify-between gap-s3">
        <div className="text-xs" aria-live="polite">
          {saving ? (
            <span
              role="status"
              className="text-gray-2"
              data-testid="confidence-log-saving"
            >
              {t('confidenceLog.saving')}
            </span>
          ) : saveError ? (
            <span
              role="alert"
              className="text-flame-deep"
              data-testid="confidence-log-error"
            >
              {saveError}
            </span>
          ) : savedAt && !dirty ? (
            <span
              role="status"
              className="text-flame-deep"
              data-testid="confidence-log-saved"
            >
              ✓ {t('confidenceLog.savedAt', { when: savedAt.slice(11, 19) })}
            </span>
          ) : (
            <span aria-hidden="true">&nbsp;</span>
          )}
        </div>
        {/* #13 — explicit Save, independent of the Check-In KPI/activities
            Save. Hitting it persists the entry (which then surfaces on the
            Dashboard's Confidence Log card). */}
        <button
          type="button"
          onClick={() => void persist()}
          disabled={saving || !dirty}
          data-testid="confidence-log-save"
          data-state={dirty ? 'dirty' : savedAt ? 'saved' : 'pristine'}
          className={
            dirty
              ? 'btn-primary text-sm transition-colors'
              : 'rounded-md px-s4 py-s2 text-sm font-semibold text-gray-2 bg-bone-3 cursor-default transition-colors'
          }
        >
          {saving
            ? t('confidenceLog.saving')
            : dirty
              ? t('confidenceLog.save.cta')
              : savedAt
                ? `✓ ${t('confidenceLog.save.saved')}`
                : t('confidenceLog.save.idle')}
        </button>
      </div>
    </section>
  );
}
